import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCopy, FaCheck, FaUsers, FaCrown, FaInfoCircle, FaTimes } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Navbar from '../components/Layout/Navbar';
import Timer from '../components/UI/Timer';
import CoinDisplay from '../components/UI/CoinDisplay';
import QuestionCard from '../components/Quiz/QuestionCard';
import PlayerVS from '../components/Quiz/PlayerVS';
import ScorePopup from '../components/Quiz/ScorePopup';
import Button from '../components/UI/Button';
import api from '../api/axios';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import useTimer from '../hooks/useTimer';
import useSound from '../hooks/useSound';

const TIME_PER_QUESTION = 20;

function Duel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const directGameId  = searchParams.get('gameId');
  const champMatchId  = searchParams.get('champMatchId');
  const champId       = searchParams.get('champId');
  const isChampMatch  = !!champMatchId;

  const { user, updateUser } = useAuth();
  const { socket, isConnected } = useSocket();
  const { playCorrect, playIncorrect, playTick, playVictory } = useSound();

  // Phase: 'lobby' | 'waiting' | 'playing' | 'result'
  const [phase, setPhase] = useState('lobby');
  const [gameId, setGameId] = useState(null);
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [opponent, setOpponent] = useState(null);
  const [myBadges, setMyBadges] = useState([]);
  const [opponentBadges, setOpponentBadges] = useState([]);
  const [myScore, setMyScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [correctAnswerIdx, setCorrectAnswerIdx] = useState(null);
  const [showScorePopup, setShowScorePopup] = useState(false);
  const [lastPoints, setLastPoints] = useState(0);
  const [copied, setCopied] = useState(false);
  const [results, setResults] = useState(null);
  const [joining, setJoining] = useState(false);
  // Démarrage manuel : les deux joueurs doivent confirmer être devant leur écran.
  // Avant, 'player-ready' partait automatiquement en rejoignant la salle, si bien
  // qu'une partie pouvait démarrer sans son créateur (resté en attente puis parti).
  const [bothPresent, setBothPresent] = useState(false);
  const [iAmReady, setIAmReady]       = useState(false);
  const [oppReady, setOppReady]       = useState(false);
  const [rematchPending, setRematchPending] = useState(false);
  const [opponentWantsRematch, setOpponentWantsRematch] = useState(false);
  const [showTutorial, setShowTutorial] = useState(
    () => !localStorage.getItem('qfl_duel_tutorial_done')
  );

  const dismissTutorial = () => {
    localStorage.setItem('qfl_duel_tutorial_done', '1');
    setShowTutorial(false);
  };

  const gameIdRef = useRef(null);
  const currentQuestionRef = useRef(null);
  const hasJoinedRef = useRef(false);
  const phaseRef = useRef(phase);

  // Sync refs
  useEffect(() => { gameIdRef.current = gameId; }, [gameId]);
  useEffect(() => { currentQuestionRef.current = currentQuestion; }, [currentQuestion]);
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // Charger les badges du joueur courant une seule fois
  useEffect(() => {
    if (!user?.id) return;
    api.get(`/profile/${user.id}`)
      .then(({ data }) => { if (data.badges) setMyBadges(data.badges); })
      .catch(() => {});
  }, [user?.id]);

  // Rejoindre directement via URL param (tournoi ou invitation ami)
  useEffect(() => {
    if (directGameId && socket && isConnected && !hasJoinedRef.current) {
      hasJoinedRef.current = true;
      if (!isChampMatch) {
        // Invitation ami : s'inscrire en base avant de rejoindre le socket
        api.post('/games/duel/join-direct', { gameId: directGameId })
          .then(() => {
            setGameId(directGameId);
            setPhase('waiting');
            socket.emit('join-game', { gameId: directGameId });
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || 'Impossible de rejoindre le duel');
            navigate('/');
          });
      } else {
        // Match de tournoi : déjà inscrit, rejoindre directement
        setGameId(directGameId);
        setPhase('waiting');
        socket.emit('join-game', { gameId: directGameId });
      }
    }
  }, [directGameId, socket, isConnected]);

  const handleReady = () => {
    if (!socket || !gameId || iAmReady) return;
    socket.emit('player-ready', { gameId });
    setIAmReady(true);
  };

  const handleTimeUp = useCallback(() => {
    if (!revealed) {
      playIncorrect();
      setRevealed(true);
      setSelectedAnswer(null);
    }
  }, [revealed, playIncorrect]);

  const { timeLeft, start, pause, reset } = useTimer(TIME_PER_QUESTION, handleTimeUp);

  // Son d'urgence
  useEffect(() => {
    if (timeLeft <= 5 && timeLeft > 0 && !revealed) playTick();
  }, [timeLeft, revealed]);

  // ── Gestion Socket ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    // Reconnexion socket : rejoindre la salle à nouveau si partie en cours
    const handleReconnect = () => {
      const gId = gameIdRef.current;
      if (gId && (phaseRef.current === 'waiting' || phaseRef.current === 'playing')) {
        console.log('[Duel] Reconnecté — rejoint la partie', gId);
        socket.emit('join-game', { gameId: gId });
      }
    };
    socket.on('connect', handleReconnect);

    // Confirmation de rejoindre la salle → signaler prêt seulement maintenant
    socket.on('join-game-success', ({ gameId: confirmedGameId, totalPlayers, gameMode }) => {
      console.log('[Duel] join-game-success, joueurs:', totalPlayers);
      if (totalPlayers >= 2) setBothPresent(true);
    });

    // Un adversaire a rejoint
    socket.on('player-joined', ({ userId: oppId, pseudo, totalPlayers }) => {
      if (pseudo !== user?.pseudo) {
        setOpponent({ pseudo, id: oppId });
        setBothPresent(true);
        toast.success(`${pseudo} a rejoint la partie !`);
        // Réveiller l'attention du joueur qui attendait : son + vibration
        playVictory();
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        // Charger les badges de l'adversaire
        if (oppId) {
          api.get(`/profile/${oppId}`)
            .then(({ data }) => { if (data.badges) setOpponentBadges(data.badges); })
            .catch(() => {});
        }
      }
    });

    // L'adversaire s'est déclaré prêt
    socket.on('player-ready-ack', ({ userId: readyId }) => {
      if (String(readyId) !== String(user?.id)) setOppReady(true);
    });

    // L'adversaire est revenu après une coupure : le forfait est annulé
    socket.on('opponent-reconnected', ({ pseudo }) => {
      toast.success(`${pseudo || 'Ton adversaire'} est revenu — le match continue !`);
    });

    // Le jeu démarre
    socket.on('game-start', ({ totalQuestions: total }) => {
      setTotalQuestions(total);
    });

    // Nouvelle question
    socket.on('next-question', ({ question, questionIndex: idx }) => {
      // Normaliser le format de la question (comme le backend retourne)
      const normalized = {
        ...question,
        _id:             question.id || question._id,
        question:        question.intitule || question.question,
        options:         Array.isArray(question.choix) ? question.choix : (question.options || []),
        reponseCorrecte: question.bonne_reponse ?? question.reponseCorrecte,
      };
      setCurrentQuestion(normalized);
      setQuestionIndex(idx - 1); // idx est 1-based côté serveur
      setSelectedAnswer(null);
      setRevealed(false);
      setCorrectAnswerIdx(null);
      reset(TIME_PER_QUESTION);
      start();
      setPhase('playing');
    });

    // Résultat d'une réponse
    socket.on('answer-result', ({ isCorrect, bonneReponse, points, totalScore }) => {
      setRevealed(true);
      setCorrectAnswerIdx(bonneReponse);
      pause();
      if (isCorrect) {
        setMyScore(totalScore);
        setLastPoints(points);
        setShowScorePopup(true);
        playCorrect();
        setTimeout(() => setShowScorePopup(false), 1200);
      } else {
        playIncorrect();
      }
    });

    // Scores en temps réel
    socket.on('score-update', ({ scores }) => {
      const me = scores.find((s) => String(s.userId) === String(user?.id) || s.pseudo === user?.pseudo);
      const opp = scores.find((s) => String(s.userId) !== String(user?.id) && s.pseudo !== user?.pseudo);
      if (me)  setMyScore(me.score);
      if (opp) { setOppScore(opp.score); setOpponent((o) => ({ ...(o || {}), pseudo: opp.pseudo })); }
    });

    // Fin de partie
    socket.on('game-end', ({ results: finalResults }) => {
      pause();
      setResults(finalResults);
      const myResult = finalResults.find((r) => String(r.userId) === String(user?.id) || r.pseudo === user?.pseudo);
      if (myResult?.rank === 1) playVictory();
      // Rafraîchir les données utilisateur depuis l'API (XP/coins persistés par le serveur)
      api.get('/auth/me').then(({ data }) => { if (data.user) updateUser(data.user); }).catch(() => {});
      setPhase('result');
    });

    // L'adversaire a quitté — on attend game-end (15s) avant d'aller en result
    socket.on('player-left', ({ userId }) => {
      if (userId !== user?.id) {
        toast.error("L'adversaire a quitté la partie");
        pause();
        // Ne pas aller en 'result' ici : results serait null → fausse ÉGALITÉ
        // game-end (émis 15s plus tard par le serveur) gère la transition
      }
    });

    // L'adversaire s'est déconnecté — compte à rebours affiché
    socket.on('opponent-disconnected', ({ gracePeriod }) => {
      toast.error(`Adversaire déconnecté — fin dans ${gracePeriod}s si absent`, { duration: gracePeriod * 1000, id: 'opp-disconnected' });
    });

    // Timeout de question : l'adversaire n'a pas répondu, on passe
    socket.on('question-timeout', () => {
      toast('⏱ Temps écoulé — question suivante', { duration: 1500 });
      setRevealed(false);
      setSelectedAnswer(null);
    });

    socket.on('error', ({ message }) => {
      toast.error(message);
    });

    // L'adversaire veut une revanche — on lui propose
    socket.on('rematch-requested', () => {
      setOpponentWantsRematch(true);
      toast('⚔️ Adversaire veut une revanche !', { duration: 25000, id: 'rematch-req' });
    });

    // Personne n'a accepté dans les 30s → on prévient
    socket.on('rematch-declined', () => {
      setRematchPending(false);
      toast.error("L'adversaire n'a pas accepté la revanche.");
    });

    // Les deux ont accepté : nouvelle partie prête
    socket.on('rematch-ready', ({ newGameId, code }) => {
      toast.dismiss('rematch-req');
      setRematchPending(false);
      setOpponentWantsRematch(false);
      hasJoinedRef.current = true;
      setGameId(newGameId);
      setRoomCode(code);
      setOpponent((o) => o); // garde l'adversaire connu
      setMyScore(0); setOppScore(0);
      setCurrentQuestion(null); setQuestionIndex(0);
      setSelectedAnswer(null); setRevealed(false); setResults(null);
      setPhase('waiting');
      socket.emit('join-game', { gameId: newGameId });
      toast.success('Revanche lancée !');
    });

    return () => {
      socket.off('connect', handleReconnect);
      socket.off('join-game-success');
      socket.off('player-joined');
      socket.off('game-start');
      socket.off('next-question');
      socket.off('answer-result');
      socket.off('score-update');
      socket.off('game-end');
      socket.off('player-left');
      socket.off('opponent-disconnected');
      socket.off('player-ready-ack');
      socket.off('opponent-reconnected');
      socket.off('question-timeout');
      socket.off('error');
      socket.off('rematch-requested');
      socket.off('rematch-declined');
      socket.off('rematch-ready');
    };
  }, [socket, user]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const handleCreateRoom = async () => {
    if (!isConnected) {
      toast.error('Serveur non connecté. Lance le backend d\'abord.');
      return;
    }
    try {
      const { data } = await api.post('/games/duel/create');
      setGameId(data.gameId);
      setRoomCode(data.code);
      setPhase('waiting');
      // Rejoindre la salle socket
      socket.emit('join-game', { gameId: data.gameId });
      // player-ready émis dans join-game-success pour éviter la race condition
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la création du duel');
    }
  };

  const handleJoinRoom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) { toast.error('Entre un code de partie'); return; }
    if (!isConnected) { toast.error('Serveur non connecté'); return; }
    setJoining(true);
    try {
      const { data } = await api.post('/games/duel/join', { code });
      setGameId(data.gameId);
      setRoomCode(data.code);
      // Rejoindre la salle socket + signaler prêt
      socket.emit('join-game', { gameId: data.gameId });
      // player-ready émis dans join-game-success pour éviter la race condition
      setPhase('waiting');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Code invalide ou duel déjà commencé');
    } finally {
      setJoining(false);
    }
  };

  const handleAnswer = useCallback((answerIndex) => {
    if (revealed || selectedAnswer !== null) return;
    pause();
    setSelectedAnswer(answerIndex);

    const gId = gameIdRef.current;
    const q   = currentQuestionRef.current;
    if (socket && gId && q) {
      socket.emit('submit-answer', {
        gameId:     gId,
        questionId: q._id || q.id,
        answer:     answerIndex,
        timeMs:     (TIME_PER_QUESTION - timeLeft) * 1000,
      });
    }
  }, [revealed, selectedAnswer, socket, pause, timeLeft]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(roomCode).then(() => {
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const resetGame = () => {
    setPhase('lobby'); setGameId(null); setRoomCode(''); setJoinCode('');
    setOpponent(null); setMyScore(0); setOppScore(0); setCurrentQuestion(null);
    setQuestionIndex(0); setSelectedAnswer(null); setRevealed(false); setResults(null);
    setRematchPending(false); setOpponentWantsRematch(false);
    toast.dismiss('rematch-req'); toast.dismiss('my-rematch');
  };

  // ── Phases d'affichage ───────────────────────────────────────────────────────
  if (phase === 'lobby') {
    return (
      <div className="app-container flex flex-col min-h-screen">
        <Navbar title="DUEL" showBack onBack={() => navigate('/')} />
        <motion.div
          className="flex-1 flex flex-col items-center justify-center px-5 gap-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col items-center gap-2 mb-4">
            <FaUsers size={48} className="text-qfl-gold" style={{ filter: 'drop-shadow(0 0 15px rgba(240,180,41,0.4))' }} />
            <h2 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
              Mode Duel
            </h2>
            <p className="text-white/50 text-sm text-center">Affronte un ami en temps réel</p>

            {/* Tooltip tutoriel — visible au premier usage */}
            <AnimatePresence>
              {showTutorial && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  style={{
                    background: 'rgba(240,180,41,0.1)',
                    border: '1px solid rgba(240,180,41,0.35)',
                    borderRadius: 14,
                    padding: '12px 14px',
                    marginTop: 8,
                    maxWidth: 300,
                    position: 'relative',
                  }}
                >
                  <button
                    onClick={dismissTutorial}
                    style={{
                      position: 'absolute', top: 8, right: 8,
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(255,255,255,0.4)',
                    }}
                  >
                    <FaTimes size={11} />
                  </button>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <FaInfoCircle size={14} color="#f0b429" style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <p style={{ color: '#f0b429', fontWeight: 700, fontSize: '0.78rem', marginBottom: 4, fontFamily: "'Rajdhani', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Comment jouer en Duel ?
                      </p>
                      <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem', lineHeight: 1.5 }}>
                        1. Crée une partie → partage le <strong style={{ color: '#fff' }}>code à 6 lettres</strong> à ton ami.<br />
                        2. Ton ami saisit le code et rejoint.<br />
                        3. La partie démarre automatiquement à 2 joueurs !
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {!isConnected && (
              <p className="text-red-400 text-xs text-center mt-1">⚠ Serveur hors ligne — lance le backend</p>
            )}
          </div>

          <Button variant="gold" size="lg" fullWidth onClick={handleCreateRoom} disabled={!isConnected}>
            CRÉER UNE PARTIE
          </Button>

          <div className="separator w-full">
            <span className="text-white/40 text-sm">ou rejoindre</span>
          </div>

          <div className="w-full flex gap-3">
            <input
              type="text"
              placeholder="Code de partie"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              className="form-input flex-1 text-center text-2xl tracking-widest font-bold uppercase"
              maxLength={8}
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            />
            <Button variant="green" size="md" onClick={handleJoinRoom} loading={joining} disabled={!isConnected}>
              OK
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (phase === 'waiting') {
    return (
      <div className="app-container flex flex-col min-h-screen">
        <Navbar title={isChampMatch ? 'MATCH' : 'DUEL'} showBack onBack={isChampMatch ? () => navigate(`/championships/${champId}`) : resetGame} />
        <div className="flex-1 flex flex-col items-center justify-center px-5 gap-8">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            className="w-20 h-20 rounded-full border-4 border-qfl-gold border-t-transparent"
          />
          {isChampMatch ? (
            <div className="text-center">
              <p className="text-qfl-gold font-black text-lg uppercase tracking-widest mb-2"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                MATCH DE TOURNOI
              </p>
              <p className="text-white/60 text-sm">
                {bothPresent ? `${opponent?.pseudo || 'Ton adversaire'} est là !` : 'En attente de ton adversaire...'}
              </p>
              <p className="text-white/30 text-xs mt-1">
                {bothPresent
                  ? 'La partie démarre quand vous êtes prêts tous les deux'
                  : 'Reste sur cet écran — tu seras prévenu dès son arrivée'}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-white/60 text-sm mb-2">En attente d'un adversaire...</p>
              <p className="text-white font-semibold text-sm">Partage ce code :</p>
            </div>
          )}
          {bothPresent && (
            <div className="w-full text-center">
              {!iAmReady ? (
                <button
                  onClick={handleReady}
                  className="btn-primary w-full py-4 text-lg font-black uppercase tracking-wider"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}
                >
                  ⚽ Je suis prêt !
                </button>
              ) : (
                <p className="text-qfl-green font-bold text-sm">
                  ✅ Tu es prêt — {oppReady ? 'lancement en cours...' : `en attente de ${opponent?.pseudo || 'ton adversaire'}...`}
                </p>
              )}
              {!iAmReady && (
                <p className="text-white/40 text-xs mt-3">
                  Clique dès que tu es installé. Personne ne démarre sans toi.
                </p>
              )}
            </div>
          )}

          {!isChampMatch && (
            <div className="bg-black/40 rounded-2xl p-5 border border-white/10 text-center w-full">
              <div
                className="text-4xl font-black text-qfl-gold tracking-[0.3em] mb-4"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                {roomCode}
              </div>
              <button
                onClick={handleCopyCode}
                className="flex items-center gap-2 mx-auto text-white/60 hover:text-white transition-colors text-sm"
              >
                {copied ? <FaCheck size={14} className="text-qfl-green" /> : <FaCopy size={14} />}
                {copied ? 'Copié !' : 'Copier le code'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (phase === 'playing') {
    return (
      <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '16px' }}>
        <Navbar
          showBack
          onBack={() => { if (window.confirm('Quitter le duel ?')) resetGame(); }}
          rightContent={<CoinDisplay coins={myScore} />}
        />
        <div className="px-4 mb-3">
          <PlayerVS
            player1={{ ...user, badges: myBadges }}
            player2={{ ...(opponent || { pseudo: 'Adversaire', niveau: 1 }), badges: opponentBadges }}
            score1={myScore}
            score2={oppScore}
            maxScore={300 * totalQuestions}
          />
        </div>
        <div className="flex justify-center py-2">
          <Timer timeLeft={timeLeft} totalTime={TIME_PER_QUESTION} size={80} />
        </div>
        <div className="flex-1 px-4 overflow-y-auto pb-4 relative">
          <AnimatePresence mode="wait">
            {currentQuestion && (
              <QuestionCard
                key={questionIndex}
                question={currentQuestion}
                questionIndex={questionIndex}
                totalQuestions={totalQuestions}
                selectedAnswer={selectedAnswer}
                correctAnswer={correctAnswerIdx}
                revealed={revealed}
                onAnswer={handleAnswer}
                disabled={revealed}
              />
            )}
          </AnimatePresence>
          <ScorePopup points={lastPoints} visible={showScorePopup} />
        </div>
      </div>
    );
  }

  if (phase === 'result') {
    const myResult   = results?.find((r) => r.pseudo === user?.pseudo || String(r.userId) === String(user?.id));
    const oppResult  = results?.find((r) => r.pseudo !== user?.pseudo && String(r.userId) !== String(user?.id));
    const playerWon  = myResult?.rank === 1;
    const scoresEqual = myResult?.score === oppResult?.score;
    const draw       = false; // le temps départage toujours

    const fmtTime = (ms) => {
      if (!ms) return '—';
      const s = Math.floor(ms / 1000);
      const m = Math.floor(s / 60);
      return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
    };

    return (
      <motion.div
        className="app-container flex flex-col min-h-screen px-5 py-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div className="flex flex-col items-center gap-4 flex-1 justify-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="text-6xl"
          >
            {draw ? '🤝' : playerWon ? '🏆' : '😔'}
          </motion.div>
          <h2
            className="text-3xl font-black uppercase tracking-widest text-center"
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              color: draw ? '#94a3b8' : playerWon ? '#f0b429' : '#ef4444',
            }}
          >
            {draw ? 'ÉGALITÉ' : playerWon ? 'VICTOIRE !' : 'DÉFAITE'}
          </h2>
          <div className="bg-black/40 rounded-2xl p-5 border border-white/10 w-full text-center">
            <div className="flex justify-around items-center">
              <div className="flex flex-col items-center gap-1">
                <div className="text-4xl font-black text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {myResult?.score ?? myScore}
                </div>
                <div className="text-white/50 text-xs">{user?.pseudo || 'Vous'}</div>
                {myResult?.totalTimeMs > 0 && (
                  <div className="text-white/30 text-xs flex items-center gap-1 mt-0.5">
                    ⏱ {fmtTime(myResult.totalTimeMs)}
                  </div>
                )}
              </div>
              <div className="text-2xl font-black text-white/30" style={{ fontFamily: "'Rajdhani', sans-serif" }}>VS</div>
              <div className="flex flex-col items-center gap-1">
                <div className="text-4xl font-black text-qfl-red" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {oppResult?.score ?? oppScore}
                </div>
                <div className="text-white/50 text-xs">{oppResult?.pseudo || opponent?.pseudo || 'Adversaire'}</div>
                {oppResult?.totalTimeMs > 0 && (
                  <div className="text-white/30 text-xs flex items-center gap-1 mt-0.5">
                    ⏱ {fmtTime(oppResult.totalTimeMs)}
                  </div>
                )}
              </div>
            </div>

            {/* Message explicatif si scores égaux */}
            {scoresEqual && (
              <div className="mt-4 rounded-xl px-3 py-2 text-xs"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="text-white/50">
                  ⚡ Scores égaux — départage par le temps de réponse
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Upsell Premium après défaite pour les non-premium */}
        {!playerWon && !draw && user?.plan !== 'premium' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl border border-yellow-500/40 p-4 mb-3"
            style={{ background: 'linear-gradient(135deg, rgba(240,180,41,0.12), rgba(26,74,46,0.6))' }}
          >
            <div className="flex items-center gap-3">
              <FaCrown size={28} className="text-qfl-gold flex-shrink-0" style={{ filter: 'drop-shadow(0 0 8px rgba(240,180,41,0.5))' }} />
              <div className="flex-1">
                <p className="font-bold text-qfl-gold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  PASSE PREMIUM
                </p>
                <p className="text-white/60 text-xs">Vies illimitées · badge couronne · parrainage</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs font-bold text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    1 000 FCFA/mois
                  </span>
                  <span className="text-white/20 text-xs">·</span>
                  <span className="text-xs text-white/40" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    10 000 FCFA/an
                  </span>
                </div>
              </div>
              <button
                onClick={() => navigate('/shop')}
                className="text-xs font-bold bg-qfl-gold text-black px-3 py-1.5 rounded-lg flex-shrink-0"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                S'ABONNER
              </button>
            </div>
          </motion.div>
        )}

        {isChampMatch && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="rounded-2xl p-4 mb-3 text-center"
            style={{
              background: playerWon ? 'rgba(240,180,41,0.1)' : 'rgba(239,68,68,0.08)',
              border: `1px solid ${playerWon ? 'rgba(240,180,41,0.3)' : 'rgba(239,68,68,0.2)'}`,
            }}
          >
            <p className="font-black text-sm uppercase tracking-widest"
              style={{ fontFamily: "'Rajdhani', sans-serif", color: playerWon ? '#f0b429' : '#ef4444' }}>
              {draw ? 'ÉGALITÉ — REJOUE' : playerWon ? 'TU PASSES AU TOUR SUIVANT !' : 'ÉLIMINÉ DU TOURNOI'}
            </p>
          </motion.div>
        )}

        <div className="flex flex-col gap-3">
          {isChampMatch ? (
            <Button variant="gold" size="lg" fullWidth onClick={() => navigate(`/championships/${champId}`)}>
              RETOUR AU BRACKET
            </Button>
          ) : (
            <Button
              variant={opponentWantsRematch ? 'green' : 'gold'}
              size="lg"
              fullWidth
              loading={rematchPending}
              onClick={() => {
                if (!gameId || !socket || rematchPending) return;
                setRematchPending(true);
                socket.emit('request-rematch', { gameId });
                if (!opponentWantsRematch) {
                  toast('⚔️ Revanche demandée — en attente de l\'adversaire...', { duration: 28000, id: 'my-rematch' });
                }
              }}
            >
              {opponentWantsRematch ? '⚔️ ACCEPTER LA REVANCHE' : '⚔️ REVANCHE'}
            </Button>
          )}
          <Button variant="outline" size="md" fullWidth onClick={resetGame}>
            NOUVELLE PARTIE
          </Button>
          <Button variant="outline" size="md" fullWidth onClick={() => navigate('/')}>
            ACCUEIL
          </Button>
        </div>
      </motion.div>
    );
  }

  return null;
}

export default Duel;
